import React, { useRef, useState, useEffect } from 'react';
import Turnstile from 'react-turnstile';
import { useCookieConsent } from '../CookieConsentProvider';
import SEO from '../SEO';
import { useLanguage } from '../../i18n';

const Contact = () => {
  const form = useRef();
  const [lastSentTime, setLastSentTime] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);

  const { allowed } = useCookieConsent();
  const { t, withLanguage } = useLanguage();
  const functionalAllowed = allowed("functional");
  const rawTurnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const turnstileSiteKey =
    typeof rawTurnstileSiteKey === "string" ? rawTurnstileSiteKey.trim() : "";
  const isTurnstileConfigured =
    Boolean(turnstileSiteKey) &&
    turnstileSiteKey !== "turnstile-site-key" &&
    turnstileSiteKey !== "undefined" &&
    turnstileSiteKey !== "null";
  const backendBaseUrl = (process.env.REACT_APP_BACKEND_BASE_URL || "").replace(/\/+$/, "");
  const contactEndpoint = backendBaseUrl.endsWith("/api")
    ? `${backendBaseUrl}/contact`
    : `${backendBaseUrl}/api/contact`;
  const canSubmit =
    consentChecked &&
    functionalAllowed &&
    isTurnstileConfigured &&
    Boolean(captchaToken);

  // Ha kikapcsolják a Functional cookie-t, reseteljük a token-t
  useEffect(() => {
    if (!functionalAllowed) {
      setCaptchaToken(null);
    }
  }, [functionalAllowed]);

  const sendEmail = async (e) => {
    e.preventDefault();

    const now = Date.now();
    if (lastSentTime && now - lastSentTime < 2 * 60 * 1000) {
      alert(t("contact.alerts.rateLimit"));
      return;
    }

    if (!functionalAllowed) {
      alert(t("contact.alerts.functionalCookies"));
      return;
    }

    if (!isTurnstileConfigured) {
      alert(t("contact.alerts.turnstileNotConfigured"));
      return;
    }

    if (!captchaToken) {
      alert(t("contact.alerts.captcha"));
      return;
    }

    if (!consentChecked) {
      alert(t("contact.alerts.privacy"));
      return;
    }

    const formData = new FormData(form.current);
    const payload = {
      title: formData.get("title"),
      name: formData.get("name"),
      email: formData.get("email"),
      message: formData.get("message"),
      token: captchaToken,
    };

    try {
      if (!backendBaseUrl) {
        alert(t("contact.alerts.backendNotConfigured"));
        return;
      }

      const response = await fetch(contactEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        alert(t("contact.alerts.success"));
        setLastSentTime(now);
        form.current.reset();
        setCaptchaToken(null);
        setConsentChecked(false);
      } else {
        alert(t("contact.alerts.errorPrefix") + result.error);
      }
    } catch (error) {
      alert(t("contact.alerts.networkErrorPrefix") + error.message);
    }
  };

  return (
    <div style={styles.container}>
      <SEO
        title={t("seo.contactTitle")}
        description={t("seo.contactDescription")}
        url="https://fixanor.com/contact"
        image="https://fixanor.com/images/hero-image-webp.webp"
      />
      <h1 style={styles.title}>{t("contact.title")}</h1>
      <form ref={form} onSubmit={sendEmail} style={styles.form}>
        <input type="text" name="title" placeholder={t("contact.placeholders.subject")} required style={styles.input} />
        <input type="text" name="name" placeholder={t("contact.placeholders.name")} required style={styles.input} />
        <input type="email" name="email" placeholder={t("contact.placeholders.email")} required style={styles.input} />
        <textarea
          name="message"
          placeholder={t("contact.placeholders.message")}
          required
          maxLength={300}
          style={styles.textarea}
        />

        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            required
          />
          &nbsp;{t("contact.consentBefore")}&nbsp;
          <a href={withLanguage("/privacy-policy")} target="_blank" rel="noopener noreferrer">{t("common.privacyPolicy")}</a>
          {t("contact.consentAfter")}
        </label>

        {/* Csak akkor rendereljük a Turnstile-t, ha Functional cookie engedélyezve van */}
        {!functionalAllowed ? (
          <p style={{ color: "var(--color-danger)", fontSize: "0.9rem" }}>
            {t("contact.functionalCookiesMessage")}{" "}
            <button
              type="button"
              onClick={() => window.openCookieSettings()}
              style={{ background: "none", border: "none", color: "var(--color-primary)", textDecoration: "underline", cursor: "pointer" }}
            >
              {t("common.cookieSettings")}
            </button>
            {t("contact.functionalCookiesSuffix")}
          </p>
        ) : !isTurnstileConfigured ? (
          <p style={{ color: "var(--color-danger)", fontSize: "0.9rem" }}>
            {t("contact.turnstileNotConfiguredNotice")}
          </p>
        ) : (
          <Turnstile
            key={functionalAllowed} // újrarenderelés cookie változásnál
            sitekey={turnstileSiteKey}
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken(null)}
            onError={() => setCaptchaToken(null)}
          />
        )}

        <button
          type="submit"
          style={{
            ...styles.button,
            opacity: canSubmit ? 1 : 0.6,
            cursor: canSubmit ? "pointer" : "not-allowed"
          }}
          disabled={!canSubmit}
        >
          {t("contact.send")}
        </button>
      </form>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '600px',
    margin: '3rem auto',
    padding: '0 1rem',
    textAlign: 'center',
    color: 'var(--color-text)',
  },
  title: {
    fontSize: '2rem',
    marginBottom: '2rem',
    color: 'var(--color-heading)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    backgroundColor: 'var(--color-input-bg)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-input-border)',
    borderRadius: '6px',
  },
  textarea: {
    padding: '0.5rem',
    fontSize: '1rem',
    minHeight: '100px',
    resize: 'none',
    backgroundColor: 'var(--color-input-bg)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-input-border)',
    borderRadius: '6px',
  },
  checkboxLabel: {
    fontSize: '0.9rem',
    textAlign: 'left',
  },
  button: {
    padding: '0.75rem',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-primary-contrast)',
    fontSize: '1rem',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '6px',
  }
};

export default Contact;
